/**
 * BatchRunCard - Individual batch run display
 */

import { Clock, CheckCircle2, XCircle, Loader2, Send, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import type { BatchRun } from '@/lib/api/batch';

interface BatchRunCardProps {
  run: BatchRun;
  onSelect?: (runId: string) => void;
  isSelected?: boolean;
}

const statusConfig: Record<string, { icon: typeof Clock; label: string; variant: 'default' | 'info' | 'warning' | 'success' | 'error'; color: string; spin?: boolean }> = {
  pending: { icon: Clock, label: 'Ausstehend', variant: 'default', color: 'text-neutral-500' },
  submitted: { icon: Send, label: 'Gesendet', variant: 'info', color: 'text-primary-500' },
  running: { icon: Loader2, label: 'In Bearbeitung', variant: 'warning', color: 'text-amber-500', spin: true },
  completed: { icon: CheckCircle2, label: 'Fertig', variant: 'success', color: 'text-emerald-500' },
  failed: { icon: XCircle, label: 'Fehlgeschlagen', variant: 'error', color: 'text-red-500' },
  cancelled: { icon: XCircle, label: 'Abgebrochen', variant: 'default', color: 'text-neutral-500' },
};

export function BatchRunCard({ run, onSelect, isSelected }: BatchRunCardProps) {
  const { t } = useTranslation();
  const config = statusConfig[run.status] || statusConfig.pending;
  const Icon = config.icon;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <button
      onClick={() => onSelect?.(run.id)}
      className={cn(
        'w-full text-left p-4 rounded-lg border transition-all duration-200',
        'hover:border-orange-300 dark:hover:border-orange-700',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
        isSelected
          ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20'
          : 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Preview Image Thumbnail (if completed) */}
        {run.preview_image_url ? (
          <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-neutral-200 dark:border-neutral-700 flex-shrink-0">
            <img
              src={run.preview_image_url}
              alt="Batch preview"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div className={cn('p-2 rounded-lg flex-shrink-0', 'bg-neutral-100 dark:bg-neutral-800')}>
            <Package className="h-5 w-5 text-orange-500" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">
                Batch #{run.id.slice(0, 8)}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {formatDate(run.created_at)}
              </p>
            </div>

            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <Badge variant={config.variant} size="sm" icon={<Icon className={cn('h-3 w-3', config.color, config.spin && 'animate-spin')} />}>
                {config.label}
              </Badge>
              <span className="text-xs text-neutral-500">
                {run.progress_total} {run.progress_total === 1 ? t('common.image') : t('common.images')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {run.reserved_credits && (
        <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center justify-between text-sm">
            <span className="text-neutral-500">Reserviert:</span>
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              {run.reserved_credits.toFixed(2)} Credits
            </span>
          </div>
        </div>
      )}

      {run.error_message && (
        <div className="mt-3 p-2 rounded bg-red-50 dark:bg-red-950/30 text-sm text-red-700 dark:text-red-300 truncate">
          {run.error_message}
        </div>
      )}
    </button>
  );
}
